<template>
  <div
    class="drawer-container"
    v-if="visible"
  >
    <Transition
      name="drawer-mask"
      appear
    >
      <div
        class="drawer-mask"
        v-if="visible"
        @click="close"
      />
    </Transition>
    <Transition
      :name="`drawer-slide-${direction}`"
      appear
    >
      <div
        v-if="contentVisible"
        class="drawer-content"
        :class="['drawer-' + direction]"
        :style="getDrawerSizeStyle()"
      >
        <div class="drawer-header">
          <div class="title">
            {{ title }}
          </div>
          <img
            @click="close"
            src="@/assets/images/close-icon.svg"
            width="24px"
            alt=""
          >
        </div>
        <div class="drawer-body">
          <slot />
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, watch, onUnmounted } from 'vue'

defineEmits(['update:visible'])

const props = defineProps({
  title: {
    type: String,
    default: ''
  },
  direction: {
    type: String,
    default: 'right'
  },
  beforeClose: {
    type: Function
  },
  size: {
    type: [String, Number],
    default: '30%'
  }
})

const visible = defineModel({ required: true })

const contentVisible = ref(false)

watch(visible, (val) => {
  if (val) {
    document.body.style.overflow = 'hidden'
    setTimeout(() => {
      contentVisible.value = val
    }, 100)
  }
})

const close = () => {
  if (props.beforeClose) {
    props.beforeClose(hide)
  } else {
    hide()
  }
}

const hide = () => {
  contentVisible.value = false
  setTimeout(() => {
    visible.value = false
    document.body.style.overflow = ''
  }, 250)
}

// 根据方向和size属性计算抽屉的样式
const getDrawerSizeStyle = () => {
  const isHorizontal = ['left', 'right'].includes(props.direction)
  const sizeProperty = isHorizontal ? 'width' : 'height'

  // 如果是数字类型，添加px单位；如果是字符串类型，直接使用
  const sizeValue = typeof props.size === 'number' ? `${props.size}px` : props.size

  return {
    [sizeProperty]: sizeValue
  }
}

onUnmounted(() => {
  document.body.style.overflow = ''
})
</script>

<style lang="less" scoped>
.drawer-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 99999999999999;
}

.drawer-mask {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
}

.drawer-content {
  position: absolute;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 10px -5px rgba(0, 0, 0, 0.2),
    0 16px 24px 2px rgba(0, 0, 0, 0.14),
    0 6px 30px 5px rgba(0, 0, 0, 0.12);
}

.drawer-right {
  top: 0;
  right: 0;
  height: 100%;
  min-width: 300px;
}

.drawer-left {
  top: 0;
  left: 0;
  height: 100%;
  min-width: 300px;
}

.drawer-top {
  top: 0;
  left: 0;
  width: 100%;
  min-height: 200px;
}

.drawer-bottom {
  bottom: 0;
  left: 0;
  width: 100%;
  min-height: 200px;
}

.drawer-header {
  display: flex;
  justify-content: space-between;
  height: 56px;
  padding: 0 16px;
  background-color: #fff;
  .title {
    display: flex;
    align-items: center;
    color: #232323;
    font-size: 20px;
    font-family: mon-700;
  }

  img {
    cursor: pointer;
  }
}

.drawer-body {
  height: 100%;
  overflow: auto;
}

/* 遮罩层淡入淡出动画 */
.drawer-mask-enter-active,
.drawer-mask-leave-active {
  transition: opacity 0.25s ease-in-out;
}
.drawer-mask-enter-from,
.drawer-mask-leave-to {
  opacity: 0;
}

/* 右侧抽屉动画 */
.drawer-slide-right-enter-active,
.drawer-slide-right-leave-active {
  transition: transform 0.25s ease-in-out;
  will-change: transform;
}
.drawer-slide-right-enter-from,
.drawer-slide-right-leave-to {
  transform: translateX(100%);
}

/* 左侧抽屉动画 */
.drawer-slide-left-enter-active,
.drawer-slide-left-leave-active {
  transition: transform 0.25s ease-in-out;
  will-change: transform;
}
.drawer-slide-left-enter-from,
.drawer-slide-left-leave-to {
  transform: translateX(-100%);
}

/* 顶部抽屉动画 */
.drawer-slide-top-enter-active,
.drawer-slide-top-leave-active {
  transition: transform 0.25s ease-in-out;
  will-change: transform;
}
.drawer-slide-top-enter-from,
.drawer-slide-top-leave-to {
  transform: translateY(-100%);
}

/* 底部抽屉动画 */
.drawer-slide-bottom-enter-active,
.drawer-slide-bottom-leave-active {
  transition: transform 0.25s ease-in-out;
  will-change: transform;
}
.drawer-slide-bottom-enter-from,
.drawer-slide-bottom-leave-to {
  transform: translateY(100%);
}
</style>
